import type { FC } from 'react';
import { useRef } from 'react';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import Label from '@components/Label';
import { Show } from '@components/Show';
import LayoutOptions from '@components/Sidebar/BlockLayoutSelector/LayoutSelector';
import { CLAUSE_TYPES } from '@root/helpers';
import { isClauseBlock } from '@root/helpers/blocks';
import { useEffectOnce } from '@src/hooks';
import { isEndorsementPath } from '@utils/app-paths';

import { useBlockCreator } from '../../context/context';
import { useBlockSetupForm } from '../../hooks/useBlockSetupForm';
import { StepView } from '../StepView';

export const StepBlockSetup: FC = () => {
  const { onPrevStep, goToStep } = useBlockCreator();
  const { formValues, isValid, onClauseReferenceChange, onClauseTypeChange, onNameChange, onTypeChange } =
    useBlockSetupForm();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffectOnce(() => {
    inputRef.current?.focus();
  });

  return (
    <StepView
      isValid={isValid}
      nextButtonLabel={'Create block'}
      onNext={() => goToStep('wysiwyg')}
      onPrev={onPrevStep}
      secondHeaderTitle="Create Block"
      stepId="block"
    >
      <div className="mx-auto flex w-2/3 flex-col gap-7">
        <InputText
          name="create-block-name-input"
          labelText="Block name"
          value={formValues?.name}
          onChange={onNameChange}
          placeholder="Block name"
          className="w-full"
          ref={inputRef}
        />
        <div>
          <div className="text-info-700 mb-2 flex justify-between text-sm font-medium">
            <Label>Block Layout</Label>
          </div>
          <LayoutOptions
            onChange={onTypeChange}
            showAllOptions={!isEndorsementPath()}
            type={formValues?.type ?? 'generic'}
          />
        </div>
        <Show when={Boolean(formValues && isClauseBlock(formValues))}>
          <div className="flex items-center justify-between gap-5">
            <InputSelect
              dataTestId="select:clause-type"
              labelText="Clause Type"
              placeholder="Select clause type"
              options={CLAUSE_TYPES.map((label) => ({ name: label, value: label }))}
              name="clause_type"
              value={formValues?.clause_type}
              onChange={onClauseTypeChange}
              inputClassName="bg-white"
            />
            <InputText
              dataTestId="text:clause-reference"
              labelText="Clause Reference"
              placeholder="Clause reference"
              name="clause_reference"
              value={formValues?.clause_reference}
              onChange={onClauseReferenceChange}
            />
          </div>
        </Show>
      </div>
    </StepView>
  );
};
